import React, { forwardRef } from 'react';
import { dynamic } from 'umi';
import type {
  IColorInfo,
  IColorPaletteProps,
  IColorPaletteRef,
} from './ColorPalette';
import ColorSwatch, { type IColorSwatchProps } from './ColorSwatch';
import './index.scss';

export type {
  IColorInfo,
  IColorPaletteProps,
  IColorPaletteRef,
  IColorSwatchProps,
};
export { ColorSwatch };

/**
 * 说明：
 * - React 的 `ref` 不是普通 props，`dynamic()` 这类 HOC 通常不会自动透传 `ref`。
 * - 这里把 `ref` 显式作为普通 prop（`forwardedRef`）传入懒加载组件，
 *   再在真正的实现组件上挂载，从而让上层能拿到 `useImperativeHandle` 暴露的方法
 *   （例如 `handleOnSubmit`）。
 */
const LazyColorPalette = dynamic({
  async loader() {
    // 这里的注释 webpackChunkName 可以指导 webpack 将该组件 HugeA 以这个名字单独拆出去
    // 并行加载组件实现和颜色选择器库，避免组件挂载后再串行请求导致二次延迟
    const [{ default: InnerColorPalette }] = await Promise.all([
      import(
        /* webpackChunkName: "dbl-custom-color-selector" */ './ColorPalette'
      ),
      // 预加载 @uiw/react-color-chrome，让静态导入在组件 chunk 加载时即完成解析
      import('@uiw/react-color-chrome'),
    ]);

    return (props: ILazyColorPaletteProps) => {
      const { forwardedRef, ...rest } = props;
      return <InnerColorPalette {...rest} ref={forwardedRef} />;
    };
  },
});

interface ILazyColorPaletteProps extends IColorPaletteProps {
  forwardedRef: React.ForwardedRef<IColorPaletteRef>;
}

const TypedLazyColorPalette =
  LazyColorPalette as React.ComponentType<ILazyColorPaletteProps>;

const ColorPalette = forwardRef<IColorPaletteRef, IColorPaletteProps>(
  (props, ref) => {
    return <TypedLazyColorPalette {...props} forwardedRef={ref} />;
  },
);

export default ColorPalette;